import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { Login, SocialLogin } from "../../../services/ApiService";
import { useState } from "react";
import { useAuth, useCart, useStore } from "../../../hooks";
import LoginWithGoogle from "../LoginWithGoogle";
import { validateEmail } from "../../../helpers";
import { HiOutlineUser } from "react-icons/hi2";
import { FaRegEnvelope } from "react-icons/fa";
import { FiEye, FiEyeOff } from "react-icons/fi";
import clsx from "clsx";
import { Link } from "react-router-dom";
import axios from "axios";
import InfiniteLoader from "../../loaders/InfiniteLoader";
import { Toast } from "../../Toast";

type FormFields = {
  email: string;
  password: string;
};

type ValidationErrors = Partial<Record<keyof FormFields, string>>;

type Props = {
  show: boolean;
  allowGuest?: boolean;
  handleClose: () => void;
  handleSuccess: () => void;
  handleSwitch: (newMode: any) => void;
};

const LoginModal: React.FC<Props> = ({
  show,
  allowGuest = true,
  handleClose,
  handleSuccess,
  handleSwitch,
}) => {
  const { storeId } = useStore();
  const { cartId } = useCart();
  const { setToken, setCustomer } = useAuth();
  const { storeLocations } = useStore();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [validated, setValidated] = useState<boolean>(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [formData, setFormData] = useState<FormFields>({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" }); // Clear the error when user types
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.email) {
      newErrors.email = "Please enter your email address";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Invalid email address";
    }

    if (!formData.password) {
      newErrors.password = "Please enter your password";
    } else if (formData.password.length < 6) {
      newErrors.password = "Invalid password";
    }

    setErrors(newErrors);
    setValidated(true);

    // Return true if there are no errors
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return false;

    setIsLoading(true);

    try {
      const { status, message, data } = await Login({
        ...formData,
        store_id: storeId,
      });
      if (status) {
        setToken(data.token);
        setCustomer(data.customer_data);
        handleSuccess();
      } else {
        Toast.error(message);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (postData: any) => {
    try {
      setIsLoading(true);
      const { status, message, data } = await SocialLogin(postData);
      if (status) handleSocialLoginSuccess(data);
      else Toast.error(message);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLoginSuccess = async (data: any) => {
    setToken(data.token);
    setCustomer(data.customer_data);
    handleSuccess();
  };

  const handleGoogleLogin = async (tokenResponse: any) => {
    console.log("Google Login Success:", tokenResponse);

    setIsLoading(true);

    try {
      const { data: userDetails } = await axios.get(
        "https://www.googleapis.com/oauth2/v3/userinfo",
        {
          headers: {
            Authorization: `Bearer ${tokenResponse.access_token}`,
          },
        },
      );

      console.log("User Details:", userDetails);

      handleSocialLogin({
        store_id: storeId,
        cart_id: cartId,
        first_name: userDetails.given_name,
        last_name: userDetails.family_name,
        name: userDetails.name,
        email: userDetails.email,
        source: "google",
        source_id: userDetails.sub,
      });
    } catch (error) {
      console.error("Google user info error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      className="login modal-mobile-bottom modal-width app-modal"
      show={show}
      onHide={handleClose}
      fullscreen="sm-down"
      centered
    >
      <Modal.Header
        closeButton
        className="login-modal-header border-0 sticky-modal-header"
      >
        <div className="w-100 text-center">
          <Link to={"/"} className="app-modal-logo-wrapper">
            <img
              className="m-auto app-modal-logo w-25"
              src={storeLocations?.store_logo}
              alt=""
            />
          </Link>
          <Modal.Title className="h4">Welcome back</Modal.Title>
          <p className="login-modal-subtitle mb-0">
            Sign in to continue your order
          </p>
        </div>
      </Modal.Header>
      {isLoading && <InfiniteLoader />}
      <Modal.Body className="login-modal-body pt-0">
        <Form
          className="login_main"
          method="POST"
          validated={validated}
          onSubmit={handleLogin}
          noValidate
        >
          {/* EMAIL */}
          <Form.Group className="mb-3">
            <Form.Label className="login-label" htmlFor="loginEmail">
              Email address
            </Form.Label>

            <div className="login-input-wrapper">
              <Form.Control
                id="loginEmail"
                type="email"
                placeholder="Enter your email address"
                name="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                isInvalid={!!errors.email}
                required
                className="login-input"
              />

              <span className="login-input-icon">
                <FaRegEnvelope size={20} />
              </span>
            </div>

            {errors.email && (
              <Form.Control.Feedback className="d-block" type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {/* PASSWORD */}
          <Form.Group className="mb-3">
            <Form.Label className="login-label" htmlFor="loginPassword">
              Password
            </Form.Label>

            <div className="login-input-wrapper">
              <Form.Control
                id="loginPassword"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                isInvalid={!!errors.password}
                required
                className="login-input pe-5"
              />

              <span
                className="login-input-icon cursor-pointer"
                onClick={togglePasswordVisibility}
              >
                {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
              </span>
            </div>

            {errors.password && (
              <Form.Control.Feedback className="d-block" type="invalid">
                {errors.password}
              </Form.Control.Feedback>
            )}
          </Form.Group>

          {/* REMEMBER */}
          <div className="login-options-row">
            <Form.Check
              id="remember_me"
              type="checkbox"
              name="remember_me"
              value="1"
              label="Keep me signed in"
            />

            <button
              type="button"
              className="forgot-link"
              onClick={() => handleSwitch("forgot-password")}
            >
              Forgot password?
            </button>
          </div>

          {/* LOGIN BTN */}
          <button type="submit" className="login_btn" disabled={isLoading}>
            {isLoading && (
              <Spinner
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="mx-2"
              />
            )}
            Sign in
          </button>

          {/* OR */}
          <div className="login-divider">
            <span>or continue with</span>
          </div>

          {/* GOOGLE */}
          <div className="d-flex justify-content-center mb-1">
            <LoginWithGoogle onSuccess={handleGoogleLogin} />
          </div>
        </Form>
      </Modal.Body>
      {/* GUEST */}
      {allowGuest && (
        <div className="guest-checkout-card">
          <div className="d-flex align-items-center justify-content-center gap-2">
            <div>
              <HiOutlineUser size={40} />
            </div>
            <div>
              <h5>Quick checkout as guest</h5>
              <p>No account needed</p>
            </div>
          </div>

          <Button className="guest-btn" onClick={() => handleSwitch("guest")}>
            Checkout as Guest
          </Button>
        </div>
      )}
      {/* REGISTER */}
      <div
        className={clsx("modal-bottom-text", allowGuest && "guest-bottom-text")}
      >
        New here?{" "}
        <span onClick={() => handleSwitch("register")}>Create Account </span>
      </div>
    </Modal>
  );
};

export default LoginModal;
